// Hook for fetching and managing Gantt data
import { useState, useEffect, useCallback } from 'react';
import { 
  GanttProject, 
  GanttTask, 
  GanttDependency, 
  GanttResource,
  GanttFilters 
} from '../types/gantt.types';
import { ganttService } from '../services/ganttService';
import { useToast } from '../../../hooks/useToast';

export const useGanttData = (filters: GanttFilters = {}) => {
  const [projects, setProjects] = useState<GanttProject[]>([]);
  const [tasks, setTasks] = useState<GanttTask[]>([]);
  const [dependencies, setDependencies] = useState<GanttDependency[]>([]);
  const [resources, setResources] = useState<GanttResource[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const { showToast } = useToast();

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      
      // Auto-unlock old tasks first (locked > 30 minutes)
      await ganttService.autoUnlockOldTasks();
      
      // Fetch all data in parallel
      const [
        projectsData, 
        tasksData, 
        depsData,
        resourcesData, 
        usersData, 
        machinesData, 
        activeUsersData
      ] = await Promise.all([
        ganttService.getProjects(filters),
        ganttService.getTasks(selectedProject || undefined, filters),
        ganttService.getDependencies(selectedProject || undefined),
        ganttService.getResources(),
        ganttService.getUsers(),
        ganttService.getMachines(),
        ganttService.getActiveUsers()
      ]);

      setProjects(projectsData);
      setTasks(tasksData);
      setDependencies(depsData);
      setResources(resourcesData);
      setUsers(usersData);
      setMachines(machinesData);
      setActiveUsers(activeUsersData);
      setLastUpdate(new Date());
      
      // Only show success on initial load
      if (loading) {
        console.log('✅ Gantt data loaded successfully');
      }
    } catch (err) {
      console.error('Error fetching Gantt data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      
      // Only show error toast on initial load or real errors
      if (loading) {
        showToast('Błąd podczas pobierania danych Gantt', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedProject, filters, loading, showToast]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch when project or filters change
  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [selectedProject, JSON.stringify(filters)]);

  // Auto-refresh every 5 seconds for real-time sync
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if not loading and no error
      if (!loading && !error) {
        console.log('🔄 Auto-refreshing Gantt data...');
        fetchData();
      }
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [fetchData, loading, error]);
  
  // Listen for visibility change to refresh when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Tab became visible, refreshing data...');
        fetchData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchData]);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connection restored, refreshing data...');
      showToast('Połączenie przywrócone', 'success');
      fetchData();
    };
    
    const handleOffline = () => {
      console.log('🔌 Connection lost');
      showToast('Utracono połączenie z serwerem', 'warning');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchData, showToast]);

  return {
    projects,
    tasks,
    dependencies,
    resources,
    users,
    machines,
    activeUsers,
    loading,
    error,
    lastUpdate,
    selectedProject,
    setSelectedProject,
    refetch: fetchData
  };
};